import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import HorsePickerInline from './HorsePickerInline';
import { FREQ_OPTIONS, FREQ_DEFAULTS, FrequencyConfig } from './CareCatalogPanel';
import { useConfirm } from '../../components/common/ConfirmDialog';

const AUTHORIZED_BY = ['Vet', 'Farm Manager', 'Owner'];
const DURATION_UNITS = [
  { value: 'days',   label: 'Day(s)' },
  { value: 'weeks',  label: 'Week(s)' },
  { value: 'months', label: 'Month(s)' },
];
const UNIT_MULTIPLIER = { days: 1, weeks: 7, months: 30 };

function computeLayupEnd(startDate, durationValue, durationUnit) {
  if (!startDate || !durationValue) return '';
  const v = parseFloat(durationValue);
  if (Number.isNaN(v)) return '';
  const days = Math.round(v * (UNIT_MULTIPLIER[durationUnit] || 1));
  const start = new Date(startDate + 'T12:00:00');
  start.setDate(start.getDate() + days);
  return start.toISOString().slice(0, 10);
}

export default function LayupModal({ open, onClose, onSaved, horse, editing, catalogItems, fromCareLog, availableHorses }) {
  const today = new Date().toLocaleDateString('sv');

  const initForm = () => ({
    reason: '',
    authorized_by: 'Vet',
    start_date: today,
    duration_value: '',
    duration_unit: 'days',
    restrictions: {
      enforce_stall_rest: false,
      pause_conditioning: false,
    },
    comments: '',
    publish_to_owner_portal: false,
  });

  const confirm = useConfirm();
  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);
  const [pickedHorseId, setPickedHorseId] = useState('');
  const [tempItems, setTempItems] = useState([]);
  const [editingTempIdx, setEditingTempIdx] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        reason: editing.reason || '',
        authorized_by: editing.authorized_by || 'Vet',
        start_date: editing.start_date || today,
        duration_value: editing.duration_value ?? (editing.duration_days ?? ''),
        duration_unit: editing.duration_unit || 'days',
        restrictions: {
          enforce_stall_rest: editing.restrictions?.enforce_stall_rest || false,
          pause_conditioning: editing.restrictions?.pause_conditioning || false,
        },
        comments: editing.comments || '',
        publish_to_owner_portal: editing.publish_to_owner_portal || false,
      });
      // Fetch linked temp items
      api.get(`/farmfolio/layups/${editing.id}/temporary-items`)
        .then(res => setTempItems(res.data || []))
        .catch(() => setTempItems([]));
    } else {
      setForm(initForm());
      setTempItems([]);
    }
    setEditingTempIdx(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const r = (key, val) => setForm(prev => ({ ...prev, restrictions: { ...prev.restrictions, [key]: val } }));

  const layupEnd = computeLayupEnd(form.start_date, form.duration_value, form.duration_unit);

  const handleSave = async () => {
    if (!form.reason.trim()) { toast.error('Reason is required'); return; }
    if (!form.start_date)     { toast.error('Start date is required'); return; }
    const targetHorseId = Array.isArray(availableHorses) ? pickedHorseId : horse?.id;
    if (Array.isArray(availableHorses) && !targetHorseId) { toast.error('Select a horse'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        horse_id: targetHorseId,
        duration_value: form.duration_value !== '' ? parseFloat(form.duration_value) : null,
        duration_days:  form.duration_value !== ''
          ? Math.round(parseFloat(form.duration_value) * (UNIT_MULTIPLIER[form.duration_unit] || 1))
          : null,
        temporary_items: tempItems,
      };
      let savedLayup;
      if (editing) {
        const res = await api.put(`/farmfolio/layups/${editing.id}`, payload);
        savedLayup = res.data;
        toast.success('Layup schedule updated');
      } else {
        const res = await api.post('/farmfolio/layups', payload);
        savedLayup = res.data;
        toast.success('Layup schedule created');
      }
      onSaved(savedLayup);
      onClose();
    } catch { toast.error('Failed to save layup'); }
    setSaving(false);
  };

  const handleEndToday = async () => {
    if (!editing) return;
    if (!await confirm({ title: 'End this layup today? Any linked temporary care will be deactivated.', destructive: true })) return;
    setSaving(true);
    try {
      const todayIso = new Date().toLocaleDateString('sv');
      // Compute duration so end date = today (start_date → today)
      const start = new Date(editing.start_date + 'T12:00:00');
      const todayDt = new Date(todayIso + 'T12:00:00');
      const days = Math.max(0, Math.round((todayDt - start) / 86400000));
      await api.put(`/farmfolio/layups/${editing.id}`, {
        is_active: false,
        duration_value: days,
        duration_unit: 'days',
        duration_days: days,
      });
      toast.success('Layup ended');
      onSaved();
      onClose();
    } catch { toast.error('Failed to end layup'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!await confirm({ title: 'Delete this layup permanently? This will also remove all linked temporary care items.', destructive: true })) return;
    setSaving(true);
    try {
      await api.delete(`/farmfolio/layups/${editing.id}`);
      toast.success('Layup deleted');
      onSaved();
      onClose();
    } catch { toast.error('Failed to delete layup'); }
    setSaving(false);
  };

  const handleAddTempItem = (item) => {
    if (editingTempIdx !== null && editingTempIdx >= 0) {
      setTempItems(prev => prev.map((it, i) => i === editingTempIdx ? { ...it, ...item } : it));
    } else {
      setTempItems(prev => [...prev, item]);
    }
    setEditingTempIdx(null);
  };

  const handleRemoveTempItem = (idx) => {
    setTempItems(prev => prev.filter((_, i) => i !== idx));
    if (editingTempIdx === idx) setEditingTempIdx(null);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {fromCareLog ? 'Care Log Entry' : (editing ? 'Edit Layup Schedule' : 'Create Layup Schedule')}
            <Badge className="bg-red-100 text-red-700 text-xs">LAYUP</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {Array.isArray(availableHorses) && (
            <HorsePickerInline availableHorses={availableHorses}
              value={pickedHorseId} onChange={setPickedHorseId} mode="single" />
          )}
          <div className="space-y-1">
            <Label>Reason / Injury *</Label>
            <Input value={form.reason} onChange={e => f('reason', e.target.value)}
              placeholder="e.g. Sprained Ankle, Post-Surgery Rest"
              data-testid="layup-reason" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Authorized By</Label>
              <Select value={form.authorized_by} onValueChange={v => f('authorized_by', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{AUTHORIZED_BY.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Start Date *</Label>
              <Input type="date" value={form.start_date} onChange={e => f('start_date', e.target.value)}
                className="time-filled" data-testid="layup-start-date" />
            </div>
          </div>

          {/* Duration — decimal + unit */}
          <div className="space-y-1">
            <Label>Duration</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                min="0"
                step="0.5"
                value={form.duration_value}
                onChange={e => f('duration_value', e.target.value)}
                placeholder="Leave blank for open-ended"
                className="flex-1"
                data-testid="layup-duration-value"
              />
              <Select value={form.duration_unit} onValueChange={v => f('duration_unit', v)}>
                <SelectTrigger className="w-28 shrink-0" data-testid="layup-duration-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{DURATION_UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {layupEnd && (
              <p className="text-xs text-muted-foreground">
                Layup ends: {new Date(layupEnd + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            )}
            {editing && (
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs mt-1"
                onClick={handleEndToday} disabled={saving}
                data-testid="layup-end-today">
                End Today
              </Button>
            )}
          </div>

          {/* Restrictions — only 2 toggles */}
          <div className="space-y-2">
            <Label>Restrictions</Label>
            <div className="space-y-2 pl-1">
              <div className="flex items-center gap-2">
                <Switch checked={form.restrictions.enforce_stall_rest}
                  onCheckedChange={v => r('enforce_stall_rest', v)}
                  data-testid="layup-restrict-stall-rest" />
                <Label className="text-sm font-normal cursor-pointer">Enforce Strict Stall Rest</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.restrictions.pause_conditioning}
                  onCheckedChange={v => r('pause_conditioning', v)}
                  data-testid="layup-restrict-pause-conditioning" />
                <Label className="text-sm font-normal cursor-pointer">Pause Active Conditioning / Training Schedule</Label>
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Comments</Label>
            <Textarea rows={3} value={form.comments} onChange={e => f('comments', e.target.value)}
              placeholder="Additional instructions or notes" />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={form.publish_to_owner_portal}
              onCheckedChange={v => f('publish_to_owner_portal', v)} />
            <Label className="cursor-pointer text-sm">Publish to Owner Portal</Label>
          </div>

          {/* Temporary Layup Schedule */}
          <div className="space-y-2 pt-3 border-t">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Temporary Layup Schedule</Label>
              {editingTempIdx === null && (
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setEditingTempIdx(-1)}
                  data-testid="layup-add-temp-item">
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              )}
            </div>

            {tempItems.length === 0 && editingTempIdx === null && (
              <p className="text-xs text-muted-foreground italic">No temporary care items yet.</p>
            )}

            {/* Saved temp items list */}
            {tempItems.map((item, idx) => (
              editingTempIdx === idx ? null : (
                <div key={item.id || idx}
                  className="flex items-center justify-between p-2 border rounded text-sm bg-muted/20">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{item.type} — {item.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {(item.frequency_type || 'once').replace('_', ' ')}
                      {item.end_date && ` · until ${new Date(item.end_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-6 w-6"
                      onClick={() => setEditingTempIdx(idx)} title="Edit">
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive"
                      onClick={() => handleRemoveTempItem(idx)} title="Remove">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )
            ))}

            {/* Inline add/edit form */}
            {editingTempIdx !== null && (
              <TempItemForm
                catalogItems={catalogItems || []}
                layupStart={form.start_date}
                layupEnd={layupEnd}
                initial={editingTempIdx >= 0 ? tempItems[editingTempIdx] : null}
                onSave={handleAddTempItem}
                onCancel={() => setEditingTempIdx(null)}
              />
            )}
          </div>
        </div>
        <div className="flex items-center justify-between pt-2 border-t mt-2">
          <div>
            {editing && (
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                onClick={handleDelete} disabled={saving}
                title="Delete layup"
                data-testid="layup-delete">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="layup-save"
              className="bg-primary hover:bg-primary/90 text-white">
              {saving ? 'Saving…' : editing ? 'Update' : 'Create Layup Schedule'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Inline form for one temporary scheduled-care item ────────────────────────
function TempItemForm({ catalogItems, layupStart, layupEnd, initial, onSave, onCancel }) {
  const [draft, setDraft] = useState(() => initial || {
    care_catalog_item_id: '',
    category: '',
    type: '',
    name: '',
    dosage: '',
    dosage_unit: '',
    units: '',
    frequency_type: 'daily',
    frequency_config: { ...FREQ_DEFAULTS.daily },
    end_date: layupEnd || '',
  });

  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }));

  const catalogCategories = [...new Set(catalogItems.map(c => c.category))].filter(Boolean).sort();
  const catalogTypes      = [...new Set(catalogItems.filter(c => c.category === draft.category).map(c => c.type))].filter(Boolean).sort();
  const filtered          = catalogItems.filter(c => c.category === draft.category && (!draft.type || c.type === draft.type));

  const handleNameSelect = (itemId) => {
    if (itemId === '__none__') {
      setDraft(p => ({ ...p, care_catalog_item_id: '', name: '', dosage: '', dosage_unit: '', units: '' }));
      return;
    }
    const item = filtered.find(c => c.id === itemId);
    if (item) {
      const ft = item.frequency_type || 'daily';
      setDraft(p => ({
        ...p,
        care_catalog_item_id: item.id,
        name: item.name,
        dosage: item.dosage || '',
        dosage_unit: item.dosage_unit || '',
        units: item.units ?? (item.serving_size ?? ''),
        frequency_type: ft,
        frequency_config: { ...(FREQ_DEFAULTS[ft] || {}), ...(item.frequency_config || {}) },
      }));
    }
  };

  const handleSubmit = () => {
    if (!draft.category) { toast.error('Category is required'); return; }
    if (!draft.type)     { toast.error('Type is required'); return; }
    if (!draft.name)     { toast.error('Select a name from the care catalog'); return; }
    let end = draft.end_date;
    if (layupEnd && end && end > layupEnd) end = layupEnd;
    onSave({
      ...draft,
      start_date: layupStart,
      end_date: end || layupEnd || null,
    });
  };

  return (
    <div className="p-3 border-2 border-primary rounded bg-white space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Category</Label>
          <Select value={draft.category || '__none__'} onValueChange={v => {
            const val = v === '__none__' ? '' : v;
            setDraft(p => ({ ...p, category: val, type: '', care_catalog_item_id: '', name: '' }));
          }}>
            <SelectTrigger className="h-8 text-xs" data-testid="temp-item-category"><SelectValue placeholder="Category…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Select —</SelectItem>
              {catalogCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Type</Label>
          <Select value={draft.type || '__none__'} onValueChange={v => {
            const val = v === '__none__' ? '' : v;
            setDraft(p => ({ ...p, type: val, care_catalog_item_id: '', name: '' }));
          }} disabled={!draft.category}>
            <SelectTrigger className="h-8 text-xs" data-testid="temp-item-type"><SelectValue placeholder="Type…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Select —</SelectItem>
              {catalogTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Name (from Care Catalog)</Label>
        <Select value={draft.care_catalog_item_id || '__none__'} onValueChange={handleNameSelect}
          disabled={!draft.type}>
          <SelectTrigger className="h-8 text-xs" data-testid="temp-item-name">
            <SelectValue placeholder={draft.type ? (filtered.length ? 'Select from catalog…' : 'No catalog items') : 'Select type first'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Select —</SelectItem>
            {filtered.map(item => {
              const detail = item.dosage
                ? `${item.dosage}${item.dosage_unit ? ' ' + item.dosage_unit : ''}`
                : item.serving_size != null
                  ? `${item.serving_size}${item.serving_size_units ? ' ' + item.serving_size_units : ''}`
                  : null;
              return (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}{detail ? ` (${detail})` : ""}{ (item.is_billable ?? item.billable) ? " (billable)" : ""}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Frequency</Label>
        <Select value={draft.frequency_type}
          onValueChange={v => setDraft(p => ({ ...p, frequency_type: v, frequency_config: { ...FREQ_DEFAULTS[v] } }))}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="p-2 border rounded bg-muted/10">
        <FrequencyConfig form={draft} setForm={setDraft} />
      </div>

      <div className="space-y-1">
        <Label className="text-xs">End Date {layupEnd && <span className="text-muted-foreground">(max {layupEnd})</span>}</Label>
        <Input type="date"
          max={layupEnd || undefined}
          value={draft.end_date || ''}
          onChange={e => set('end_date', e.target.value)}
          className={`h-8 text-xs ${draft.end_date ? 'time-filled' : ''}`}
          data-testid="temp-item-end-date" />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="button" size="sm" onClick={handleSubmit} data-testid="temp-item-save"
          className="bg-primary hover:bg-primary/90 text-white">
          {initial ? 'Update' : 'Add'}
        </Button>
      </div>
    </div>
  );
}
